import { Divider, Link, Stack, Typography, useTheme } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { CardWithTitle } from "@common/CardWithTitle/CardWithTitle";
import { ContractOfferDetails } from "./ContractOfferDetails";
import {
    EDomain,
    EStatus,
    IBroadbandSubscription,
    IMobileSubscription,
} from "@pages/AccountDetails/interface/IAccountDetailsPage";
import { AddonsOfferDetails } from "./AddonsOfferDetails";
import { ESimOfferDetails } from "./ESimOfferDetails";
import { getSubscriptionTags } from "./utils";
// eslint-disable-next-line import/named
import { useHistory } from "react-router-dom";
import { URLS } from "@constants";
import { useStyle } from "./style";

interface IParams {
    domain: EDomain;
    subscription: IMobileSubscription & IBroadbandSubscription;
}

export const OfferDetails = (props: IParams) => {
    const {
        domain,
        subscription,
        subscription: {
            addons,
            combiDiscountAmount,
            eSim,
            service,
            offer: { description, monthlyAmount },
            status,
            terminationRequestScheduledAt,
        },
    } = props;
    const serviceName = service?.name;
    const serviceId = service?.id;
    const { classes } = useStyle();
    const { t } = useTranslation(["accountDetails", "glossary", "common"]);
    const theme = useTheme();
    const { push } = useHistory();
    const isMobileDomain = domain === EDomain.MOBILE;
    const isBroadbandInitial = !isMobileDomain && status === EStatus.INITIAL;
    const offerSummary = isMobileDomain
        ? t("accountDetails:offerSummaryMobile")
        : t("accountDetails:offerSummaryFiber");

    const FooterComponent = (
        <>
            <Typography align="center" color="secondary" fontWeight={400} variant="large">
                <Trans
                    i18nKey="terminationScheduled"
                    ns="accountDetails"
                    values={{ date: terminationRequestScheduledAt, domain: isMobileDomain ? "Mobile" : "Broadband" }}
                />
            </Typography>
            {!isMobileDomain && (
                <Stack
                    alignItems="center"
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="center"
                    marginTop={1}
                    spacing={{ xs: 0, sm: 1 }}
                >
                    <Typography color="secondary" variant="normal">
                        {t("accountDetails:changeMind")}
                    </Typography>
                    <Link
                        className={classes.link}
                        underline="always"
                        onClick={() => push(`${URLS.CANCEL_BROADBAND_PAGE_URL}`)}
                    >
                        <Typography align="center" color="primary" variant="normal">
                            {t("accountDetails:stayConnected")}
                        </Typography>
                    </Link>
                </Stack>
            )}
        </>
    );

    const SummaryComponent = (
        <Stack alignItems="center" gap={2}>
            <Typography align="center" color="primary" variant="h3">
                {t("common:price", { price: monthlyAmount })} {t("glossary:aMonthForLife")}
            </Typography>
            <Typography align="center" color="secondary" variant="small">
                {offerSummary}
            </Typography>
            {combiDiscountAmount && (
                <Typography align="center" color="secondary" fontWeight={450} variant="small">
                    <Trans i18nKey="combiDiscount" ns="accountDetails" values={{ price: combiDiscountAmount }} />
                </Typography>
            )}
        </Stack>
    );

    return (
        <CardWithTitle
            FooterComponent={terminationRequestScheduledAt ? FooterComponent : undefined}
            SummaryComponent={isBroadbandInitial ? undefined : SummaryComponent}
            footerBackgroundColor={terminationRequestScheduledAt ? theme.palette.background.main : undefined}
            subheader={isBroadbandInitial || !serviceName ? t("accountDetails:serviceNameUndefined") : serviceName}
            tags={getSubscriptionTags(isMobileDomain, subscription, t)}
            title={description}
        >
            {isBroadbandInitial ? (
                SummaryComponent
            ) : (
                <Stack marginTop={2} spacing={2}>
                    <Divider />
                    <ContractOfferDetails isMobileDomain={isMobileDomain} subscription={subscription} />
                    {serviceName && serviceId && eSim && (
                        <ESimOfferDetails eSimDetails={eSim} serviceId={serviceId} serviceName={serviceName} />
                    )}
                    {addons && addons.length > 0 && (
                        <>
                            <Divider />
                            <AddonsOfferDetails addons={addons} isMobileDomain={isMobileDomain} />
                        </>
                    )}
                </Stack>
            )}
        </CardWithTitle>
    );
};
